import { useEffect, useState } from "react";
import {
  fetchSchedulerSlots,
  fetchSchedulerVotes,
  fetchUserSchedulerVote,
} from "../../../lib/data/schedulers";
import {
  hasSubmittedSchedulerVote,
  hasRespondedToScheduler,
} from "../../../lib/vote-utils";
import {
  canViewOtherVotesForUser,
  canViewVoterIdentities,
  resolveVoteVisibility,
} from "../../../lib/vote-visibility";

export function useSchedulerAttendance(participatingSchedulers, currentUserId = null) {
  const [slotsByScheduler, setSlotsByScheduler] = useState({});
  const [votesByScheduler, setVotesByScheduler] = useState({});
  const [votersByScheduler, setVotersByScheduler] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!participatingSchedulers.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
      setSlotsByScheduler({});
      setVotesByScheduler({});
      setVotersByScheduler({});
      setLoading(false);
      return;
    }

    const fetchSlotsAndVotes = async () => {
      setLoading(true);
      const slotsMap = {};
      const votesMap = {};
      const votersMap = {};

      await Promise.all(
        participatingSchedulers.map(async (scheduler) => {
          try {
            const slots = await fetchSchedulerSlots(scheduler.id);
            slotsMap[scheduler.id] = slots;

            const voteVisibility = resolveVoteVisibility(scheduler?.voteVisibility);
            // On a FINALIZED session an explicit RSVP counts as "responded" for
            // attendance even with no slot vote (RSVP'd straight on the finalized
            // poll). On OPEN polls only slot votes count (RSVPs don't exist yet).
            const isFinalized = String(scheduler?.status || "").toUpperCase() === "FINALIZED";
            const isCreator =
              currentUserId &&
              String(scheduler?.creatorId || "").trim() === String(currentUserId).trim();
            let ownVoteDoc = null;
            let hasVoted = false;

            const canReadVoteDetails = canViewOtherVotesForUser({
              voteVisibility,
              isCreator,
              hasVoted: false,
              allParticipantsVoted: scheduler?.votesAllSubmitted === true,
              isFinalized: String(scheduler?.status || "").toUpperCase() === "FINALIZED",
            });
            const canReadVoterIdentities = canViewVoterIdentities({
              isCreator,
              hideVoterIdentities: scheduler?.hideVoterIdentities,
            });
            let canReadPeerVoteStatus = canReadVoteDetails || canReadVoterIdentities;

            if (!canReadPeerVoteStatus && currentUserId) {
              ownVoteDoc = await fetchUserSchedulerVote(scheduler.id, currentUserId);
              hasVoted = hasSubmittedSchedulerVote(ownVoteDoc);
              const hasVoteDetailAccessAfterVote = canViewOtherVotesForUser({
                voteVisibility,
                isCreator,
                hasVoted,
                allParticipantsVoted: scheduler?.votesAllSubmitted === true,
                isFinalized: String(scheduler?.status || "").toUpperCase() === "FINALIZED",
              });
              canReadPeerVoteStatus = hasVoteDetailAccessAfterVote || canReadVoterIdentities;
            }

            if (!canReadPeerVoteStatus) {
              // `votes` includes the viewer's own RSVP-only doc (finalized) so
              // their own attendance shows; `voters` stays strict (slot vote only,
              // `hasVoted`) so an own RSVP doesn't flip the identity-reveal gate.
              const ownResponded = hasRespondedToScheduler(ownVoteDoc, { isFinalized });
              votesMap[scheduler.id] = ownResponded && ownVoteDoc ? [ownVoteDoc] : [];
              const ownSlotVotes = hasVoted && ownVoteDoc ? [ownVoteDoc] : [];
              votersMap[scheduler.id] = ownSlotVotes
                .map((voteDoc) => ({
                  id: voteDoc.id,
                  email: voteDoc.userEmail,
                  avatar: voteDoc.userAvatar,
                }))
                .filter((voter) => voter.id || voter.email);
              return;
            }

            const voteDocs = await fetchSchedulerVotes(scheduler.id);
            // `votes` = who RESPONDED (slot vote OR, on a finalized poll, an
            // explicit RSVP) — drives the finalized attendance summary.
            votesMap[scheduler.id] = (voteDocs || []).filter((voteDoc) =>
              hasRespondedToScheduler(voteDoc, { isFinalized })
            );
            // `voters` = who cast a SLOT vote — drives the voting-progress UI AND
            // the HIDDEN_WHILE_VOTING identity-reveal gate. An RSVP must NOT count
            // here: RSVPing should never unlock peers' vote identities. Keep strict.
            votersMap[scheduler.id] = (voteDocs || [])
              .filter((voteDoc) => hasSubmittedSchedulerVote(voteDoc))
              .map((voteDoc) => ({
                id: voteDoc.id,
                email: voteDoc.userEmail,
                avatar: voteDoc.userAvatar,
              }))
              .filter((voter) => voter.id || voter.email);
          } catch (err) {
            console.error(`Failed to fetch data for scheduler ${scheduler.id}:`, err);
          }
        })
      );

      setSlotsByScheduler(slotsMap);
      setVotesByScheduler(votesMap);
      setVotersByScheduler(votersMap);
      setLoading(false);
    };

    fetchSlotsAndVotes();
  }, [currentUserId, participatingSchedulers]);

  return { slotsByScheduler, votesByScheduler, votersByScheduler, loading };
}
